
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@/components/ui/link-compat';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useForgotPassword } from '@/hooks/usePasswordReset';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthShell, AuthCard } from '@/components/auth/auth-shell';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type FormData = z.infer<typeof schema>;

function ForgotPasswordPage() {
  usePageTitle('Forgot Password');
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const { mutateAsync, isPending } = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      setFormError('');
      await mutateAsync(data.email);
      setSubmitted(true);
    } catch {
      setFormError('Something went wrong. Please try again.');
    }
  };

  return (
    <AuthShell variant="minimal">
      <AuthCard
        title="Forgot Password"
        subtitle="Enter your email and we'll send you a link to reset your password."
      >
        {submitted ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                If that email is registered, you will receive a password reset link shortly. Check your inbox.
              </span>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              <Link href="/login" className="text-primary hover:underline">
                Back to Sign In
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {formError && (
              <div className="flex items-start gap-2.5 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? 'Sending…' : 'Send Reset Link'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link href="/login" className="text-primary hover:underline">
                Back to Sign In
              </Link>
            </p>
          </form>
        )}
      </AuthCard>
    </AuthShell>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/forgot-password')({ component: ForgotPasswordPage });
